import React from 'react';
import { Star, Sun, Moon } from 'lucide-react';
import KundliChart from './KundliChart';
import SectionHeader from './SectionHeader';
import { FREE_KUNDLI_TRANSLATIONS } from '../../../../languages/freeKundli';
import { getPlanetName } from '../../../../utils/planetNames';
import {
    SunPlanetIcon,
    MoonPlanetIcon,
    MarsPlanetIcon,
    MercuryPlanetIcon,
    JupiterPlanetIcon,
    VenusPlanetIcon,
    SaturnPlanetIcon,
    RahuPlanetIcon,
    KetuPlanetIcon,
    AscendantIcon
} from './AstroIcons';

interface KundliTabProps {
    kundliData: any;
    language: 'english' | 'hindi';
}

const KundliTab: React.FC<KundliTabProps> = ({ kundliData, language }) => {
    const planets = kundliData.planets || kundliData.D1?.planets || {};
    const asc = kundliData.D1?.ascendant || kundliData.ascendant;
    const langKey = language === 'hindi' ? 'Hindi' : 'English';
    const t = FREE_KUNDLI_TRANSLATIONS[langKey];

    // Helper function to get planet icon
    const getPlanetIcon = (name: string, className: string = "w-4 h-4") => {
        const lowerName = name.toLowerCase();
        if (lowerName.includes('sun') || lowerName.includes('surya')) return <SunPlanetIcon className={className} />;
        if (lowerName.includes('moon') || lowerName.includes('chandra')) return <MoonPlanetIcon className={className} />;
        if (lowerName.includes('mars') || lowerName.includes('mangal')) return <MarsPlanetIcon className={className} />;
        if (lowerName.includes('mercury') || lowerName.includes('budh')) return <MercuryPlanetIcon className={className} />;
        if (lowerName.includes('jupiter') || lowerName.includes('guru') || lowerName.includes('brihaspati')) return <JupiterPlanetIcon className={className} />;
        if (lowerName.includes('venus') || lowerName.includes('shukra')) return <VenusPlanetIcon className={className} />;
        if (lowerName.includes('saturn') || lowerName.includes('shani')) return <SaturnPlanetIcon className={className} />;
        if (lowerName.includes('rahu') || lowerName.includes('north node')) return <RahuPlanetIcon className={className} />;
        if (lowerName.includes('ketu') || lowerName.includes('south node')) return <KetuPlanetIcon className={className} />;
        return <Star size={16} className="text-primary-400" />;
    };

    // Transform planets for KundliChart
    const transformPlanets = (planetsData: any) => {
        if (!planetsData || Object.keys(planetsData).length === 0) return [];

        return Object.entries(planetsData).map(([name, data]: [string, any]) => {
            const p = data;
            return {
                planet: name,
                house: p.house || p['house-num'] || p.house_no || 1,
                rashi: p.sign || p.rashi || '',
                longitude: typeof p.degree === 'number' ? p.degree.toFixed(2) : p.degree,
                nakshatra: typeof p.nakshatra === 'object' ? p.nakshatra.name : p.nakshatra,
                pada: typeof p.nakshatra === 'object' ? p.nakshatra.pada : p.pada
            };
        });
    };

    const d1Planets = kundliData.planets || kundliData.D1?.planets || {};
    const d9Planets = kundliData.D9?.planets || {};

    return (
        <div className="space-y-8 animate-in zoom-in-95 duration-500">
            {/* D1 and D9 Charts Side by Side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* D1 Lagna Chart */}
                <div className="bg-white/[0.08] border border-white/20 rounded-[24px] md:rounded-[32px] p-5 md:p-8">
                    <SectionHeader
                        icon={Sun}
                        title={t.lagnaChart || "Lagna Chart"}
                        description={t.lagnaChartDesc || "Birth chart showing planetary positions at the time of birth."}
                    />
                    <div className="mt-4">
                        <KundliChart
                            key="D1"
                            language={language}
                            planets={transformPlanets(d1Planets)}
                        />
                    </div>
                </div>

                {/* D9 Navamsa Chart */}
                <div className="bg-white/[0.08] border border-white/20 rounded-[24px] md:rounded-[32px] p-5 md:p-8">
                    <SectionHeader
                        icon={Moon}
                        title={t.navamsaChart || "Navamsa Chart"}
                        description={t.navamsaChartDesc || "D9 divisional chart for marriage, fortune, and spiritual growth."}
                    />
                    <div className="mt-4">
                        <KundliChart
                            key="D9"
                            language={language}
                            planets={transformPlanets(d9Planets)}
                        />
                    </div>
                </div>
            </div>

            {/* Planets Table */}
            <div className="bg-white/[0.08] border border-white/20 rounded-[24px] md:rounded-[32px] overflow-hidden shadow-2xl">
                <div className="p-5 md:p-8 pb-4">
                    <SectionHeader icon={Star} title={t.planetaryPositions} description={t.planetsDesc} />
                </div>

                {/* Desktop Table View */}
                <div className="hidden md:block overflow-x-auto">
                    <table className="w-full text-left font-sans">
                        <thead>
                            <tr className="bg-primary-950/50 text-[10px] uppercase tracking-widest font-black text-primary-300 border-b border-white/20">
                                <th className="px-6 py-5">{t.planet}</th>
                                <th className="px-6 py-5 text-center">{t.sign}</th>
                                <th className="px-6 py-5 text-center">{t.degree}</th>
                                <th className="px-6 py-5 text-center">{t.house}</th>
                                <th className="px-6 py-5 text-center">{t.nakshatra}</th>
                                <th className="px-6 py-5 text-center">{t.pada}</th>
                                <th className="px-6 py-5 text-center">{t.dignity}</th>
                                <th className="px-6 py-5 text-center">{t.nature}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                            {/* Ascendant Row */}
                            {asc && (
                                <tr className="bg-primary-500/10 font-semibold border-l-4 border-l-primary-500">
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-lg bg-primary-500/20 flex items-center justify-center">
                                                <AscendantIcon className="w-5 h-5" />
                                            </div>
                                            <span className="font-bold text-white text-sm">{t.ascendant}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-center text-primary-200 text-sm">{asc.sign}</td>
                                    <td className="px-6 py-4 text-center font-mono text-primary-300 text-xs">{asc.degree}</td>
                                    <td className="px-6 py-4 text-center text-white text-sm">1</td>
                                    <td className="px-6 py-4 text-center text-primary-200 text-sm">{typeof asc.nakshatra === 'object' ? asc.nakshatra.name : asc.nakshatra}</td>
                                    <td className="px-6 py-4 text-center text-primary-200 text-sm">{typeof asc.nakshatra === 'object' ? asc.nakshatra.pada : asc.pada}</td>
                                    <td className="px-6 py-4 text-center text-sm">---</td>
                                    <td className="px-6 py-4 text-center text-sm">---</td>
                                </tr>
                            )}

                            {Object.entries(planets).map(([name, p]: [string, any], idx: number) => {
                                const isMalefic = ['Sun', 'Mars', 'Saturn', 'Rahu', 'Ketu'].some(m => name.includes(m));
                                const degree = p.pos?.dec_deg ? p.pos.dec_deg.toFixed(2) : (p.degree || '-');
                                const dignity = p['house-rel'] || p.status || p.dignity || '---';

                                return (
                                    <tr key={idx} className="hover:bg-white/5 transition-colors group">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
                                                    {getPlanetIcon(name, "w-5 h-5")}
                                                </div>
                                                <span className="font-bold text-white text-sm">{getPlanetName(name, t.planetNames)}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-center text-primary-200 text-sm">{p.sign || '-'}</td>
                                        <td className="px-6 py-4 text-center font-mono text-primary-300 text-xs">{degree}°</td>
                                        <td className="px-6 py-4 text-center text-white text-sm">{p.house || p['house-num'] || '-'}</td>
                                        <td className="px-6 py-4 text-center text-primary-200 text-sm">{typeof p.nakshatra === 'object' ? p.nakshatra.name : (p.nakshatra || '-')}</td>
                                        <td className="px-6 py-4 text-center text-primary-200 text-sm">{typeof p.nakshatra === 'object' ? p.nakshatra.pada : (p.pada || '-')}</td>
                                        <td className="px-6 py-4 text-center">
                                            <span className={`px-2 py-1 rounded text-xs font-bold ${dignity === 'Exalted' || dignity === 'Own' ? 'bg-green-500/20 text-green-400' :
                                                dignity === 'Debilitated' ? 'bg-red-500/20 text-red-400' :
                                                    'text-primary-200/60'}`}>
                                                {dignity}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <span className={`px-2 py-1 rounded text-xs font-bold ${isMalefic ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'}`}>
                                                {isMalefic ? t.malefic : t.benefic}
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* Mobile Card View */}
                <div className="md:hidden divide-y divide-white/10">
                    {/* Ascendant Card */}
                    {asc && (
                        <div className="p-4 bg-primary-500/10 border-l-4 border-l-primary-500">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="w-10 h-10 rounded-xl bg-primary-500/20 flex items-center justify-center">
                                    <AscendantIcon className="w-6 h-6" />
                                </div>
                                <div>
                                    <span className="font-bold text-white">{t.ascendant}</span>
                                    <p className="text-xs text-primary-300">{asc.sign}</p>
                                </div>
                            </div>
                            <div className="grid grid-cols-3 gap-2 text-center">
                                <div className="bg-white/5 rounded-lg p-2">
                                    <span className="text-[9px] text-primary-200/40 block">{t.degree}</span>
                                    <span className="text-xs font-bold text-white">{asc.degree}</span>
                                </div>
                                <div className="bg-white/5 rounded-lg p-2">
                                    <span className="text-[9px] text-primary-200/40 block">{t.nakshatra}</span>
                                    <span className="text-xs font-bold text-white truncate block">{typeof asc.nakshatra === 'object' ? asc.nakshatra.name : asc.nakshatra}</span>
                                </div>
                                <div className="bg-white/5 rounded-lg p-2">
                                    <span className="text-[9px] text-primary-200/40 block">{t.pada}</span>
                                    <span className="text-xs font-bold text-white">{typeof asc.nakshatra === 'object' ? asc.nakshatra.pada : asc.pada}</span>
                                </div>
                            </div>
                        </div>
                    )}

                    {Object.entries(planets).map(([name, p]: [string, any], idx: number) => {
                        const isMalefic = ['Sun', 'Mars', 'Saturn', 'Rahu', 'Ketu'].some(m => name.includes(m));
                        const degree = p.pos?.dec_deg ? p.pos.dec_deg.toFixed(2) : (p.degree || '-');
                        const dignity = p['house-rel'] || p.status || p.dignity || '---';

                        return (
                            <div key={idx} className="p-4">
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                                        {getPlanetIcon(name, "w-6 h-6")}
                                    </div>
                                    <div>
                                        <span className="font-bold text-white">{getPlanetName(name, t.planetNames)}</span>
                                        <p className="text-xs text-primary-200">{p.sign || '-'} • {t.house} {p.house || p['house-num'] || '-'}</p>
                                    </div>
                                    <div className="ml-auto">
                                        <span className={`px-2 py-1 rounded text-[10px] font-bold ${isMalefic ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'}`}>
                                            {isMalefic ? t.malefic : t.benefic}
                                        </span>
                                    </div>
                                </div>
                                <div className="grid grid-cols-4 gap-2 text-center">
                                    <div className="bg-white/5 rounded-lg p-2">
                                        <span className="text-[9px] text-primary-200/40 block">{t.degree}</span>
                                        <span className="text-xs font-bold text-white">{degree}°</span>
                                    </div>
                                    <div className="bg-white/5 rounded-lg p-2">
                                        <span className="text-[9px] text-primary-200/40 block">{t.nakshatra}</span>
                                        <span className="text-xs font-bold text-white truncate block">{typeof p.nakshatra === 'object' ? p.nakshatra.name : (p.nakshatra || '-')}</span>
                                    </div>
                                    <div className="bg-white/5 rounded-lg p-2">
                                        <span className="text-[9px] text-primary-200/40 block">{t.pada}</span>
                                        <span className="text-xs font-bold text-white">{typeof p.nakshatra === 'object' ? p.nakshatra.pada : (p.pada || '-')}</span>
                                    </div>
                                    <div className="bg-white/5 rounded-lg p-2">
                                        <span className="text-[9px] text-primary-200/40 block">{t.dignity}</span>
                                        <span className={`text-xs font-bold ${dignity === 'Exalted' || dignity === 'Own' ? 'text-green-400' : dignity === 'Debilitated' ? 'text-red-400' : 'text-white'}`}>
                                            {dignity}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default KundliTab;
